import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState, Fragment } from "react";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  ArrowLeft,
  Loader2,
  Copy,
  Check,
  Filter,
  ChevronDown,
  ChevronUp,
  FileX,
} from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { verifyAvatarsPolicies } from "@/lib/storage-policy-check.functions";

export const Route = createFileRoute("/_authenticated/storage-check")({
  component: StorageCheckPage,
});

function StorageCheckPage() {
  const { isAdmin, adminChecked, loading } = useAuth();
  const verifyFn = useServerFn(verifyAvatarsPolicies);

  const query = useQuery({
    queryKey: ["admin", "storage-policy-check", "avatars"],
    queryFn: () => verifyFn(),
    enabled: !!isAdmin,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  if (loading || !adminChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        loading…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        admin access required
      </div>
    );
  }

  const data = query.data;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between gap-3 mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            back to admin
          </Link>
          <button
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border hover:bg-muted transition-colors disabled:opacity-60"
          >
            {query.isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            re-run checks
          </button>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight">avatars bucket — policy verification</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          runs live INSERT / SELECT / UPDATE / DELETE against the <code className="px-1 py-0.5 rounded bg-muted text-xs">avatars</code> bucket as your admin user, then verifies that cross-folder writes are denied.
        </p>

        {query.isLoading && (
          <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            running checks…
          </div>
        )}

        {query.error && (
          <div className="mt-8 p-4 rounded border border-destructive/40 bg-destructive/10 text-sm text-destructive">
            {(query.error as Error).message}
          </div>
        )}

        {data && <ResultsPanel data={data} />}
      </div>
    </div>
  );
}

function ResultsPanel({ data }: { data: Awaited<ReturnType<typeof verifyAvatarsPolicies>> }) {
  const [filter, setFilter] = useState<"all" | "pass" | "fail">("all");
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const allOk = data.results.every((r) => r.ok);
  const failCount = data.results.filter((r) => !r.ok).length;
  const passCount = data.results.length - failCount;

  const filtered = useMemo(() => {
    if (filter === "pass") return data.results.filter((r) => r.ok);
    if (filter === "fail") return data.results.filter((r) => !r.ok);
    return data.results;
  }, [data.results, filter]);

  const toggleExpand = (i: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const copyDetail = async (text: string, i: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(i);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <InfoCard label="bucket" value={data.bucket?.id ?? "—"} />
        <InfoCard label="public" value={data.bucket ? (data.bucket.public ? "yes" : "no") : "—"} />
        <InfoCard label="acting user" value={data.userId.slice(0, 8) + "…"} mono />
      </div>

      <div
        className={`mt-6 p-4 rounded border text-sm font-medium ${
          allOk
            ? "border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-300"
            : "border-destructive/40 bg-destructive/10 text-destructive"
        }`}
      >
        {allOk
          ? `all ${data.results.length} checks passed`
          : `${failCount} of ${data.results.length} checks failed`}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Filter className="w-4 h-4" />
          <span>filter</span>
        </div>
        <div className="flex rounded border border-border overflow-hidden">
          <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>
            all ({data.results.length})
          </FilterButton>
          <FilterButton active={filter === "pass"} onClick={() => setFilter("pass")}>
            pass ({passCount})
          </FilterButton>
          <FilterButton active={filter === "fail"} onClick={() => setFilter("fail")}>
            fail ({failCount})
          </FilterButton>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left px-4 py-3 font-medium text-muted-foreground w-10">status</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">check</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground w-24">operation</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground w-16">details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <FileX className="w-6 h-6 opacity-40" />
                    <span>no results for this filter</span>
                  </div>
                </td>
              </tr>
            )}
            {filtered.map((r, rawIdx) => {
              const isExpanded = expandedRows.has(rawIdx);
              const hasDetails = !!r.details;
              const copyText = `${r.name}\noperation: ${r.operation}\nstatus: ${r.ok ? "PASS" : "FAIL"}\nmessage: ${r.message}${r.details ? `\ndetails: ${r.details}` : ""}`;

              return (
                <Fragment key={rawIdx}>
                  <tr
                    className={`border-b border-border/60 last:border-b-0 hover:bg-muted/30 transition-colors ${
                      !r.ok ? "bg-destructive/5" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      {r.ok ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-destructive" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-muted-foreground text-xs mt-0.5">{r.message}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {r.operation}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => copyDetail(copyText, rawIdx)}
                          className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground"
                          title="copy result"
                        >
                          {copiedId === rawIdx ? (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                        {hasDetails && (
                          <button
                            onClick={() => toggleExpand(rawIdx)}
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground"
                            title="toggle details"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-3.5 h-3.5" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && hasDetails && (
                    <tr className="border-b border-border/60">
                      <td colSpan={4} className="px-4 py-3">
                        <pre className="text-xs text-muted-foreground/80 whitespace-pre-wrap break-words font-mono bg-muted/50 p-3 rounded">
                          {r.details}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.testPath && (
        <p className="mt-4 text-xs text-muted-foreground">
          test path used: <code className="font-mono">{data.testPath}</code>
        </p>
      )}
    </>
  );
}

function InfoCard({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="p-3 rounded border border-border bg-card">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono" : "font-medium"}`}>{value}</div>
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
        active ? "bg-foreground text-background" : "hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}
